/**
 * F-003 — Playfield container.
 *
 * Defines the 3D region where planets live. Walls are invisible to the
 * camera but exist for physics collision (cannon-es bodies arrive in F-004).
 * The limit line sits at 80% of the playfield height; planets stacking
 * above it for sustained time triggers game-over (F-017, BR-004).
 *
 * Debug visualization (wireframe walls + a magenta limit-line ribbon) is
 * controllable via ``setDebugVisible`` so the smoke build stays clean while
 * authors iterate locally.
 */

import {
  Box3,
  BoxGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Vector3,
  WireframeGeometry,
  type Object3D,
} from "three";

const DEFAULT_WIDTH = 12;
const DEFAULT_HEIGHT = 18;
const DEFAULT_DEPTH = 8;
const LIMIT_LINE_RATIO = 0.8;

export interface PlayfieldBounds {
  width: number;
  height: number;
  depth: number;
}

export interface Playfield {
  bounds: PlayfieldBounds;
  limitLineY: number;
  group: Group;
  /** Toggle wireframe walls + limit-line ribbon visibility. */
  setDebugVisible(visible: boolean): void;
  /** Returns a fresh Box3 representing the playfield extents. */
  toBox3(): Box3;
}

function buildWalls(bounds: PlayfieldBounds): LineSegments {
  const box = new BoxGeometry(bounds.width, bounds.height, bounds.depth);
  const wireframe = new WireframeGeometry(box);
  const material = new LineBasicMaterial({
    color: 0x4a3aff,
    transparent: true,
    opacity: 0.35,
  });
  const lines = new LineSegments(wireframe, material);
  lines.name = "playfield-walls";
  return lines;
}

function buildLimitRibbon(bounds: PlayfieldBounds, y: number): Mesh {
  const plane = new PlaneGeometry(bounds.width, 0.06);
  const material = new MeshBasicMaterial({
    color: 0xff4ddc,
    transparent: true,
    opacity: 0.65,
    depthWrite: false,
  });
  const ribbon = new Mesh(plane, material);
  ribbon.name = "playfield-limit-ribbon";
  ribbon.position.set(0, y, 0);
  return ribbon;
}

export interface CreatePlayfieldOptions {
  bounds?: Partial<PlayfieldBounds>;
  debugVisible?: boolean;
}

export function createPlayfield(
  options: CreatePlayfieldOptions = {},
): Playfield {
  const bounds: PlayfieldBounds = {
    width: options.bounds?.width ?? DEFAULT_WIDTH,
    height: options.bounds?.height ?? DEFAULT_HEIGHT,
    depth: options.bounds?.depth ?? DEFAULT_DEPTH,
  };
  if (bounds.width <= 0 || bounds.height <= 0 || bounds.depth <= 0) {
    throw new Error(
      `createPlayfield: bounds must be positive (got ${bounds.width}x${bounds.height}x${bounds.depth})`,
    );
  }
  const limitLineY = bounds.height * LIMIT_LINE_RATIO - bounds.height / 2;

  const group = new Group();
  group.name = "playfield";
  const walls = buildWalls(bounds);
  const ribbon = buildLimitRibbon(bounds, limitLineY);
  group.add(walls);
  group.add(ribbon);

  const debugObjects: Object3D[] = [walls, ribbon];
  const initialDebug = options.debugVisible ?? false;
  for (const obj of debugObjects) {
    obj.visible = initialDebug;
  }

  const setDebugVisible = (visible: boolean): void => {
    for (const obj of debugObjects) {
      obj.visible = visible;
    }
  };

  const toBox3 = (): Box3 => {
    const half = new Vector3(bounds.width / 2, bounds.height / 2, bounds.depth / 2);
    return new Box3(half.clone().negate(), half);
  };

  return { bounds, limitLineY, group, setDebugVisible, toBox3 };
}

export const __testing = {
  DEFAULT_WIDTH,
  DEFAULT_HEIGHT,
  DEFAULT_DEPTH,
  LIMIT_LINE_RATIO,
};
